// Reacting to a friend's shared tasting, for every list that shows them: one call at a time per
// share, the edge's answer handed back to the list, and a failure kept per share so it shows inside
// that card and nowhere else.
import type { ReactionKind, TastingShareView } from "@proef/contract";
import { useState } from "react";
import { useT } from "../i18n";
import { deleteShareReaction, putShareReaction } from "./tastingSharesApi";

export interface ShareReactions {
  /** Shares whose reaction is on its way. */
  readonly reacting: ReadonlySet<string>;
  readonly errors: ReadonlyMap<string, string>;
  /** Gives a reaction, or with null takes the given one back; `apply` gets the share as it is now. */
  readonly react: (shareId: string, kind: ReactionKind | null) => void;
}

export function useShareReactions(apply: (share: TastingShareView) => void): ShareReactions {
  const { t } = useT();
  const [reacting, setReacting] = useState<ReadonlySet<string>>(new Set());
  const [errors, setErrors] = useState<ReadonlyMap<string, string>>(new Map());

  const react = (shareId: string, kind: ReactionKind | null): void => {
    setReacting((held) => new Set(held).add(shareId));
    setErrors((held) => {
      const next = new Map(held);
      next.delete(shareId);
      return next;
    });
    (kind === null ? deleteShareReaction(shareId) : putShareReaction(shareId, kind))
      .then((updated) => {
        apply(updated);
        return undefined;
      })
      .catch(() => {
        setErrors((held) => new Map(held).set(shareId, t("social.reaction.error")));
      })
      .finally(() => {
        setReacting((held) => {
          const next = new Set(held);
          next.delete(shareId);
          return next;
        });
      });
  };

  return { reacting, errors, react };
}
